<template>
  <div class="br-admin">
    <BusinessDocumentsAdd
      v-if="isShowAdd"
      :key="'br-add_' + empCompanyId"
      :from-admin="true"
      :emp-company-id="empCompanyId"
      :account-id="supplierAccountId"
      :fallback-name-en="adminFallbackNameEn"
      :fallback-name-zh="adminFallbackNameZh"
      submit-type="approval"
      @hide-add="handleHideAdd"
      @submitted="handleAddSubmitted"
    />

    <div v-show="!isShowAdd" class="br-admin__container">
      <!-- 仅列表首次加载用 overlay；留言保存无整页遮罩，靠按钮 busy（上传中…） -->
      <b-overlay
        :show="loading"
        :fixed="loading"
        rounded="sm"
        z-index="2000"
      >
        <div class="br-admin__stack">
          <div class="br-admin__card">
            <BrPageHeader
              title-tag="h2"
              :title="$t('lbl_EMPBE_BusinessDocuments')"
              :description="$t('lbl_EMPBE_BR_Page_Desc')"
              :add-disabled="!empCompanyId||showApprovalActions"
              @add="goAddDocument"
            />

            <div class="br-list">
              <BrListSection
                variant="admin"
                :title="$t('lbl_EMPBE_BR_List_Section_CurrentDisplay')"
                :items="currentDisplayItems"
                key-prefix="current"
                is-current-display
                :show-empty="!loading && !hasBrApprovedDisplayDocument(documentList)"
              >
                <template #item="{ item }">
                  <BrAdminListCard
                    v-bind="adminCardProps(item, true)"
                    v-on="adminCardListeners(item)"
                    :open-mode="showApprovalActions?`approval`:`edit`"
                  />
                </template>
                <template #empty>
                  <BrEmptyBanner />
                </template>
              </BrListSection>

              <BrListSection
                v-if="recordItems.length"
                variant="admin"
                :title="$t('lbl_EMPBE_BR_List_Section_Records')"
                :items="recordItems"
                key-prefix="record"
              >
                <template #item="{ item }">
                  <BrAdminListCard
                    v-bind="adminCardProps(item, false)"
                    v-on="adminCardListeners(item)"
                    :open-mode="showApprovalActions?`approval`:`edit`"
                  />
                </template>
              </BrListSection>
            </div>
          </div>

          <BrAdminNotesPanel
            v-if="empCompanyId"
            :emp-company-id="empCompanyId"
          />
        </div>
      </b-overlay>
    </div>

    <BrImagePreviewModal
      v-model="showPreview"
      :src="previewSrc"
      :item="previewItem"
      appearance="admin"
    />

    <BrConfirmModal
      :visible="showAlert"
      :title="alertMessage"
      :confirm-label="$t('lbl_EMPBE_BR_Admin_AlertOk')"
      hide-cancel
      :close-on-scrim="false"
      @cancel="closeAlert"
      @confirm="closeAlert"
    />

    <BrConfirmModal
      :visible="showConfirm"
      :title="confirmTitleHtml"
      :body="confirmBodyHtml"
      :cancel-label="$t('lbl_cancel')"
      :confirm-label="confirmOkLabel"
      :confirm-variant="confirmOkVariant"
      :submitting="!!processingId"
      @cancel="closeConfirm"
      @confirm="confirmProcess"
    />

    <BrNameChangeModal
      :visible="showNameChangeModal"
      :document-list="documentList"
      :source-approval-id="nameChangeSourceApprovalId"
      :current-display-name-en="currentNameEn"
      :current-display-name-local="currentNameZh"
      :emp-company-id="empCompanyId"
      :account-id="supplierAccountId"
      submit-type="edit"
      @close="closeNameChangeModal"
      @submitted="handleNameChangeSubmitted"
    />

    <BrConfirmModal
      :visible="showSetAsCurrentModal"
      :title="$t('lbl_EMPBE_BR_SetAsCurrent_ConfirmTitle')"
      :body="$t('lbl_EMPBE_BR_SetAsCurrent_ConfirmBody')"
      :cancel-label="$t('lbl_EMPBE_BR_SetAsCurrent_Cancel')"
      :confirm-label="$t('lbl_EMPBE_BR_SetAsCurrent_Update')"
      :submitting="setAsCurrentSubmitting"
      @cancel="closeSetAsCurrentModal"
      @confirm="confirmSetAsCurrent"
    />

    <BrConfirmModal
      :visible="showSetAsCurrentSuccess"
      :title="$t('lbl_EMPBE_BR_SetAsCurrent_SuccessTitle')"
      :body="$t('lbl_EMPBE_BR_SetAsCurrent_SuccessBody')"
      :confirm-label="$t('lbl_EMPBE_BR_SetAsCurrent_Ok')"
      hide-cancel
      :close-on-scrim="false"
      @cancel="showSetAsCurrentSuccess = false"
      @confirm="showSetAsCurrentSuccess = false"
    />

    <BrConfirmModal
      :visible="showNameChangeSuccess"
      :title="nameChangeSuccessTitle"
      :body="nameChangeSuccessBody"
      :confirm-label="$t('lbl_EMPBE_BR_NameChange_SuccessOk')"
      hide-cancel
      :close-on-scrim="false"
      @cancel="closeNameChangeSuccess"
      @confirm="closeNameChangeSuccess"
    />

    <BrConfirmModal
      :visible="showCancelConfirm"
      :title="$t('lbl_EMPBE_BR_CancelConfirm_Title')"
      :body="$t('lbl_EMPBE_BR_CancelConfirm_Body')"
      :cancel-label="$t('lbl_EMPBE_BR_CancelConfirm_Keep')"
      :confirm-label="$t('lbl_EMPBE_BR_CancelConfirm_Ok')"
      :submitting="cancelProcessingId != null"
      @cancel="closeCancelConfirm"
      @confirm="confirmCancelSubmission"
    />

    <BrConfirmModal
      :visible="showInvalidateConfirm"
      :title="$t('lbl_EMPBE_BR_InvalidateConfirm_Title')"
      :body="invalidateConfirmBodyHtml"
      :cancel-label="$t('lbl_cancel')"
      :confirm-label="$t('lbl_EMPBE_BR_Invalidate')"
      confirm-variant="reject"
      :submitting="invalidateProcessingId != null"
      @cancel="closeInvalidateConfirm"
      @confirm="confirmInvalidate"
    />
  </div>
</template>

<script>
import BrListSection from '../../businessDocuments/components/BrListSection.vue';
import BrNameChangeModal from '../../businessDocuments/components/BrNameChangeModal.vue';
import BrConfirmModal from '../../businessDocuments/components/BrConfirmModal.vue';
import BrEmptyBanner from '../../businessDocuments/components/BrEmptyBanner.vue';
import BrImagePreviewModal from '../../businessDocuments/components/BrImagePreviewModal.vue';
import BrPageHeader from '../../businessDocuments/components/BrPageHeader.vue';
import BusinessDocumentsAdd from '../../businessDocuments/addDocument.vue';
import BrAdminListCard from './components/BrAdminListCard.vue';
import BrAdminNotesPanel from './components/BrAdminNotesPanel.vue';
import brSetAsCurrent from '../../businessDocuments/mixins/brSetAsCurrent';
import brNameChangeFlow from '../../businessDocuments/mixins/brNameChangeFlow';
import brImagePreview from '../../businessDocuments/mixins/brImagePreview';
import brCancelRejected from '../../businessDocuments/mixins/brCancelRejected';
import brAdminDocumentsLoad from './mixins/brAdminDocumentsLoad';
import brAdminApprovalActions from './mixins/brAdminApprovalActions';
import { invalidateKybAccessGateCache } from '../../../constants/businessRegistration';
import { isBrPlaceholderName } from '../../businessDocuments/utils/brCompanyNames';
import {
  getBrBusinessRegistrationId,
  canBrAdminInvalidateItem,
  resolveBrInvalidateFromStatusLabelKey,
} from '../../businessDocuments/utils/brDocument';
import { BusinessRegistrationService } from '../../../_services';
import { resolveApiErrorMessage } from '../../../_helpers/apiResponse';

export default {
  name: 'SupplierBusinessDocuments',
  mixins: [
    brNameChangeFlow,
    brSetAsCurrent,
    brImagePreview,
    brCancelRejected,
    brAdminDocumentsLoad,
    brAdminApprovalActions,
  ],
  components: {
    BrListSection,
    BrNameChangeModal,
    BrConfirmModal,
    BrEmptyBanner,
    BrImagePreviewModal,
    BrPageHeader,
    BusinessDocumentsAdd,
    BrAdminListCard,
    BrAdminNotesPanel,
  },
  props: {
    supplierId: { type: [String, Number], default: null },
    /** 管理端列表列資料；含 empCompanyId（supplierId 實際常為 accountId） */
    simulatedSupplier: { type: Object, default: null },
    /** 是否显示底部审核区（Reject / Proceed / 驳回原因）；后续只读页可传 false */
    showApprovalActions: { type: Boolean, default: true },
  },
  data() {
    return {
      /** 与产品 isShowDetail 同模式：Tab 内切换列表 / 上传 */
      isShowAdd: false,
      showInvalidateConfirm: false,
      invalidateTargetId: null,
      invalidateProcessingId: null,
      invalidateFromStatusLabel: '',
    };
  },
  computed: {
    invalidateConfirmBodyHtml() {
      const from = this.invalidateFromStatusLabel
        || this.$t('lbl_EMPBE_BR_Status_ApprovedOnly');
      const to = this.$t('lbl_EMPBE_BR_Status_Invalid');
      return this.$t('lbl_EMPBE_BR_InvalidateConfirm_Body', { from, to });
    },
    adminFallbackNameEn() {
      const fromCache = String(this.cachedCurrentNameEn || '').trim();
      if (!isBrPlaceholderName(fromCache)) {
        return fromCache;
      }
      const fromCurrent = String(this.currentNameEn || '').trim();
      if (!isBrPlaceholderName(fromCurrent)) {
        return fromCurrent;
      }
      const s = this.simulatedSupplier || {};
      return String(s.companyNameEn || s.CompanyNameEn || '').trim();
    },
    adminFallbackNameZh() {
      const fromCache = String(this.cachedCurrentNameZh || '').trim();
      if (!isBrPlaceholderName(fromCache)) {
        return fromCache;
      }
      const fromCurrent = String(this.currentNameZh || '').trim();
      if (!isBrPlaceholderName(fromCurrent)) {
        return fromCurrent;
      }
      const s = this.simulatedSupplier || {};
      return String(
        s.companyNameSc || s.CompanyNameSc
        || s.companyNameTc || s.CompanyNameTc
        || s.companyNameZh || s.CompanyNameZh
        || '',
      ).trim();
    },
  },
  methods: {
    adminCardProps(item, isCurrentDisplay) {
      return {
        item,
        draft: this.drafts[item.approvalId],
        displayNameEn: this.currentNameEn,
        displayNameLocal: this.currentNameZh,
        thumbUrl: this.resolveBrThumbUrl(item),
        isCurrentDisplay,
        canEditName: this.canRequestNameChange,
        showActions: this.showApprovalActions && this.shouldShowAdminActions(item),
        processing: this.processingId === item.approvalId,
        proceedLocked: this.isProceedLocked(item),
        templates: this.getRejectTemplatesForItem(item),
        showDirectRejectBar: this.shouldShowDirectRejectBar(item),
        canChangeToReject: this.canChangeToReject(item),
        isLastInEffectApproved: this.isLastInEffectApprovedItem(item),
        expiryContextList: this.documentList,
      };
    },
    adminCardListeners(item) {
      return {
        preview: this.openBrImagePreview,
        'edit-name': this.openNameChangeModal,
        'set-as-current': this.openSetAsCurrentModal,
        'update:rejected': (rejected) => this.setRejectSelected(item, rejected),
        'update:reason': (value) => this.setRejectReasonText(item.approvalId, value),
        'clear-reason-error': () => this.clearRejectReasonError(item.approvalId),
        proceed: () => this.onProceed(item),
        'reject-last-blocked': this.onRejectLastBlocked,
        reupload: this.goAddDocument,
        cancel: this.handleCancel,
        invalidate: this.handleInvalidate,
      };
    },
    goAddDocument() {
      if (!this.empCompanyId) {
        return;
      }
      this.isShowAdd = true;
    },
    buildSoftDeletePayload(businessRegistrationId) {
      return {
        businessRegistrationId,
        empCompanyId: this.empCompanyId,
      };
    },
    async afterCancelRejectedSuccess() {
      this.$toast?.success?.(this.$t('lbl_EMPBE_BR_Cancel_Success'));
      await this.loadPending();
    },
    /** 编辑模式 ⋯ → Invalidate：确认后 AdminInvalidateDocument */
    handleInvalidate(item) {
      if (!item || this.invalidateProcessingId != null || this.showInvalidateConfirm) {
        return;
      }
      if (!canBrAdminInvalidateItem(item)) {
        return;
      }
      /* 唯一已生效：与唯一驳回同文案 */
      if (this.isLastInEffectApprovedItem?.(item)) {
        this.openAlert(this.$t('lbl_EMPBE_BR_Admin_RejectLastBlocked'));
        return;
      }
      const businessRegistrationId = getBrBusinessRegistrationId(item);
      if (!businessRegistrationId || !this.empCompanyId) {
        this.$toast?.error?.(this.$t('lbl_EMPBE_BR_Invalidate_Failed'));
        return;
      }
      const isCurrentDisplay = Array.isArray(this.currentDisplayItems)
        && this.currentDisplayItems.some((row) => getBrBusinessRegistrationId(row) === businessRegistrationId);
      const labelKey = resolveBrInvalidateFromStatusLabelKey(item, { isCurrentDisplay });
      this.invalidateFromStatusLabel = this.$t(labelKey);
      this.invalidateTargetId = businessRegistrationId;
      this.showInvalidateConfirm = true;
    },
    closeInvalidateConfirm() {
      if (this.invalidateProcessingId != null) {
        return;
      }
      this.showInvalidateConfirm = false;
      this.invalidateTargetId = null;
      this.invalidateFromStatusLabel = '';
    },
    async confirmInvalidate() {
      const businessRegistrationId = this.invalidateTargetId;
      if (!businessRegistrationId || this.invalidateProcessingId != null || !this.empCompanyId) {
        return;
      }
      this.invalidateProcessingId = businessRegistrationId;
      try {
        const res = await BusinessRegistrationService.AdminInvalidateDocument({
          businessRegistrationId,
          empCompanyId: this.empCompanyId,
        });
        if (!res?.isSuccessful) {
          this.$toast?.error?.(
            this.localizeAdminProcessError(
              resolveApiErrorMessage(res) || this.$t('lbl_EMPBE_BR_Invalidate_Failed'),
            ),
          );
          return;
        }
        this.showInvalidateConfirm = false;
        this.invalidateTargetId = null;
        this.invalidateFromStatusLabel = '';
        this.$toast?.success?.(this.$t('lbl_EMPBE_BR_Invalidate_Success'));
        invalidateKybAccessGateCache();
        await this.loadPending();
      } catch (e) {
        this.$toast?.error?.(e?.message || this.$t('lbl_EMPBE_BR_Invalidate_Failed'));
      } finally {
        this.invalidateProcessingId = null;
      }
    },
    handleHideAdd() {
      this.isShowAdd = false;
    },
    handleAddSubmitted() {
      this.isShowAdd = false;
      this.$nextTick(() => {
        this.loadPending();
      });
    },
    async afterNameChangeSubmitted() {
      await this.loadPending();
    },
    onSetAsCurrentInvalidOpen() {
      this.$toast?.error?.(this.$t('lbl_EMPBE_BR_SetAsCurrent_Failed'));
    },
    buildSetAsCurrentPayload(approvalId) {
      if (!this.empCompanyId) {
        return null;
      }
      return {
        approvalId,
        empCompanyId: this.empCompanyId,
      };
    },
    async afterSetAsCurrentSuccess({ nameEn, nameZh }) {
      if (nameEn) this.cachedCurrentNameEn = nameEn;
      if (nameZh) this.cachedCurrentNameZh = nameZh;
      invalidateKybAccessGateCache();
      await this.loadPending();
    },
  },
};
</script>

<style lang="scss">
@import '../../businessDocuments/styles/br-shared.scss';
@import './styles/_br-admin-actions.scss';
</style>
